// Time formatting helpers — locked to a single canonical format across the app.
// All times are displayed in Vegas (PT) regardless of the browser's timezone.

function vegasParts(d: Date): { h: number; m: number } {
  // Use Intl to convert the UTC date into Vegas wall-clock components.
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const h = parseInt(parts.find(p => p.type === "hour")?.value ?? "0");
  const m = parseInt(parts.find(p => p.type === "minute")?.value ?? "0");
  return { h, m };
}

export function fmtBlockTime(d: Date): string {
  // "7:00PM" / "12:32AM" — uppercase, no space, always PT.
  const { h, m } = vegasParts(d);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, "0")}${suffix}`;
}

export function fmtAxisHour(d: Date): string {
  // "7PM", "12AM", "5:30AM" — drops minutes when on the hour, always PT.
  const { h, m } = vegasParts(d);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${m.toString().padStart(2, "0")}${suffix}`;
}

export function fmtDateBadge(d: Date): string {
  // "MAY 15"
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

// Vegas timezone fixed at PDT (UTC-7) during the festival.
// Sets are stored in DD JSON as naive ISO with no offset; we attach -07:00 here.
export function vegasIsoToDate(iso: string): Date {
  // iso looks like "2026-05-15T17:00" — append PDT offset
  if (!/[Z+\-]\d{2}:\d{2}$/.test(iso)) {
    return new Date(iso + "-07:00");
  }
  return new Date(iso);
}

// Compute festival day (1/2/3) from a UTC Date.
// Day 1 = sets that start before noon May 16 PT (Vegas time).
export function festivalDay(d: Date): 1 | 2 | 3 {
  // Compare in PT — convert by treating clock hours in -07:00.
  const utcMillis = d.getTime();
  const may16noonPT = new Date("2026-05-16T12:00:00-07:00").getTime();
  const may17noonPT = new Date("2026-05-17T12:00:00-07:00").getTime();
  if (utcMillis < may16noonPT) return 1;
  if (utcMillis < may17noonPT) return 2;
  return 3;
}

export const DAY_LABELS: Record<1 | 2 | 3, string> = {
  1: "FRIDAY",
  2: "SATURDAY",
  3: "SUNDAY",
};

export const DAY_DATES: Record<1 | 2 | 3, string> = {
  1: "MAY 15",
  2: "MAY 16",
  3: "MAY 17",
};
